import { useState } from 'react'
import {
  LBAlert,
  LBAlertDescription,
  LBAlertTitle,
  LBBadge,
  LBButton,
  LBCard,
  LBCardContent,
  LBCheckbox,
  LBDialog,
  LBDialogContent,
  LBDialogDescription,
  LBDialogFooter,
  LBDialogHeader,
  LBDialogTitle,
  lbToast
} from '@linearb/buzz'
import {
  useUpdateOrganizationSettings,
  useUpdateOrganizationFeatureFlags
} from '../../hooks/queries/useOrganizations'
import { useUpdateBackofficeVisibility } from '../../hooks/queries/useSettingsDefinitions'
import { trackAction } from '../../lib/datadogRum'

const SettingChangeConfirmationDialog = ({
  settingDefinition,
  organizationId,
  open,
  onClose,
  onSettingsUpdated,
  organizationName
}) => {
  const updateSettings = useUpdateOrganizationSettings()
  const updateFeatureFlags = useUpdateOrganizationFeatureFlags()
  const updateBackofficeVisibility = useUpdateBackofficeVisibility()

  // Set when an admin is flipping a flag the normal rules would block — the reason
  // is the restriction being bypassed (a status no role of theirs may change).
  const adminOverrideReason = settingDefinition?.adminOverrideReason ?? null
  const [acknowledged, setAcknowledged] = useState(false)

  const handleClose = () => {
    setAcknowledged(false)
    onClose()
  }

  const onAgreeClicked = async () => {
    const { isFeatureFlags = false, isBackofficeVisibleUpdate = false } =
      settingDefinition

    try {
      if (isBackofficeVisibleUpdate) {
        // Updating is_backoffice_visible field (GLOBAL setting)
        await updateBackofficeVisibility.mutateAsync({
          definitionId: settingDefinition.definition_id,
          isBackofficeVisible: settingDefinition.value
        })
      } else {
        // Regular setting value update
        const mutation = isFeatureFlags ? updateFeatureFlags : updateSettings

        // For feature flags, convert boolean to Python-compatible string (backend uses ast.literal_eval)
        // Python expects "True"/"False" (capitalized), not "true"/"false"
        // For regular settings, parse the JSON string value
        const value = isFeatureFlags
          ? settingDefinition.value
            ? 'True'
            : 'False'
          : JSON.parse(settingDefinition.value)

        const payload = {
          definition_id: settingDefinition.definition_id,
          name: settingDefinition.name,
          value
        }

        await mutation.mutateAsync({ organizationId, payload })
      }

      // value/from_value are safe booleans for feature flags and the
      // backoffice-visibility toggle, but arbitrary (possibly sensitive)
      // support-entered JSON for regular settings — only send the former.
      const valueContext =
        isFeatureFlags || isBackofficeVisibleUpdate
          ? {
              value: settingDefinition.value,
              fromValue: settingDefinition.from_value
            }
          : {}
      trackAction('setting_changed', {
        organizationId,
        organizationName,
        settingId: settingDefinition.definition_id,
        settingName: settingDefinition.name,
        ...valueContext,
        isFeatureFlags,
        isBackofficeVisibleUpdate
      })

      setAcknowledged(false)
      onSettingsUpdated()

      lbToast({
        title: 'Success',
        description: `Setting "${settingDefinition.name}" was updated`,
        variant: 'success'
      })
    } catch (error) {
      const detail =
        error?.response?.data?.detail || error?.message || 'Unknown error'
      lbToast({
        title: 'Error',
        description: `Failed to update setting "${settingDefinition.name}": ${detail}`,
        variant: 'destructive'
      })
    }
  }

  // Backoffice-visibility changes are global (no org); every other change is
  // org-scoped and still requires an organizationId.
  if (
    !settingDefinition ||
    (!organizationId && !settingDefinition.isBackofficeVisibleUpdate)
  ) {
    return null
  }

  const isPending =
    updateSettings.isPending ||
    updateFeatureFlags.isPending ||
    updateBackofficeVisibility.isPending

  // Helper function to format value for display
  const formatValueForDisplay = value => {
    if (value === null || value === undefined) {
      return 'null'
    }
    // For backoffice visibility updates, show user-friendly labels
    if (settingDefinition.isBackofficeVisibleUpdate) {
      return value ? 'Visible' : 'Hidden'
    }
    // If it's already a string that looks like JSON, parse it first
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        return JSON.stringify(parsed, null, 2)
      } catch {
        // Not valid JSON, return as-is
        return value
      }
    }
    // If it's an object, stringify with formatting
    return JSON.stringify(value, null, 2)
  }

  return (
    <LBDialog
      open={
        open &&
        settingDefinition.definition_id &&
        (organizationId || settingDefinition.isBackofficeVisibleUpdate)
      }
      onOpenChange={isOpen => !isOpen && handleClose()}
    >
      <LBDialogContent>
        <LBDialogHeader>
          <LBDialogTitle>Confirm settings update</LBDialogTitle>
          <LBDialogDescription>
            Are you sure you want to change{' '}
            {settingDefinition.isBackofficeVisibleUpdate
              ? 'backoffice visibility for'
              : 'setting'}{' '}
            <strong>{settingDefinition.name}</strong>
            {organizationName ? (
              <>
                {' '}
                for <strong>{organizationName}</strong>
              </>
            ) : null}
          </LBDialogDescription>
        </LBDialogHeader>
        <div className='space-y-3 py-4'>
          <div className='flex items-start gap-3'>
            <div className='flex-1 space-y-2'>
              <LBBadge variant='destructive'>From</LBBadge>
              <LBCard className='bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'>
                <LBCardContent className='p-3'>
                  <div className='font-mono text-sm text-red-900 dark:text-red-100 whitespace-pre-wrap'>
                    {formatValueForDisplay(settingDefinition.from_value)}
                  </div>
                </LBCardContent>
              </LBCard>
            </div>
            <div className='text-muted-foreground mt-8'>→</div>
            <div className='flex-1 space-y-2'>
              <LBBadge
                variant='default'
                className='bg-green-600 dark:bg-green-400'
              >
                To
              </LBBadge>
              <LBCard className='bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'>
                <LBCardContent className='p-3'>
                  <div className='font-mono text-sm text-green-900 dark:text-green-100 whitespace-pre-wrap'>
                    {formatValueForDisplay(settingDefinition.value)}
                  </div>
                </LBCardContent>
              </LBCard>
            </div>
          </div>
        </div>
        {adminOverrideReason && (
          <div className='space-y-3 pb-2'>
            <LBAlert variant='destructive'>
              <LBAlertTitle>Admin override</LBAlertTitle>
              <LBAlertDescription>
                {adminOverrideReason}. You are bypassing the normal restriction
                as an admin.
              </LBAlertDescription>
            </LBAlert>
            <label className='flex items-center gap-2 text-sm cursor-pointer'>
              <LBCheckbox
                checked={acknowledged}
                onCheckedChange={checked => setAcknowledged(checked === true)}
              />
              <span>I understand and want to proceed</span>
            </label>
          </div>
        )}
        <LBDialogFooter>
          <LBButton
            variant='outline'
            onClick={handleClose}
            disabled={isPending}
          >
            Disagree
          </LBButton>
          <LBButton
            onClick={onAgreeClicked}
            disabled={
              isPending || Boolean(adminOverrideReason && !acknowledged)
            }
          >
            {isPending ? 'Saving...' : 'Agree'}
          </LBButton>
        </LBDialogFooter>
      </LBDialogContent>
    </LBDialog>
  )
}

export default SettingChangeConfirmationDialog
